import React, { useState } from "react";
import { CheckCircle2, X, Activity, Clock, User, Hash, Calendar, Droplets, Heart, Wind, Thermometer, Weight, Ruler, Zap, ChevronDown } from "lucide-react";

/* --- helpers ------------------------------------------- */
const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (!isNaN(date.getTime())) return date;
  const fallback = new Date(String(value).replace(/\s+/g, "T"));
  return !isNaN(fallback.getTime()) ? fallback : null;
};
const formatDate = (iso) => {
  const date = parseDateValue(iso);
  if (!date) return "�";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
};
const formatTime = (iso) => {
  const date = parseDateValue(iso);
  if (!date) return "�";
  return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
};
const getBPStatus = (s, d) => {
  if (!s || !d) return null;
  if (s >= 140 || d >= 90) return "critical";
  if (s >= 130 || d >= 85) return "warning";
  return "normal";
};
const getTempStatus = (t) => {
  if (!t) return null;
  if (t < 36 || t > 39) return "critical";
  if (t < 36.5 || t > 38.5) return "warning";
  return "normal";
};
const getHRStatus = (hr) => {
  if (!hr) return null;
  return (hr > 100 || hr < 60) ? "critical" : "normal";
};
const getSpo2Status = (v) => {
  if (v == null) return null;
  return v < 95 ? "critical" : "normal";
};
const displayMeasurement = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "object") {
    if (value.systolic != null || value.diastolic != null) return `${value.systolic ?? "—"}/${value.diastolic ?? "—"}`;
    return Object.entries(value).map(([key, item]) => `${key}: ${item}`).join(" · ");
  }
  return String(value);
};
const CORE_MEASUREMENT_KEYS = new Set(["m001", "m002", "m003", "m004", "m005", "m006", "m007", "m008", "m051", "legacy-notes"]);

const STATUS = {
  critical: { dot: "#ef4444", text: "#dc2626", bg: "#fef2f2", border: "#fecaca", badge: "#fee2e2", badgeText: "#991b1b" },
  warning:  { dot: "#f59e0b", text: "#d97706", bg: "#fffbeb", border: "#fde68a", badge: "#fef3c7", badgeText: "#92400e" },
  normal:   { dot: "#10b981", text: "#059669", bg: "#f0fdf4", border: "#bbf7d0", badge: "#dcfce7", badgeText: "#065f46" },
};

const resolveAge = (p) => {
  const raw = p?.age ?? p?.patientAge ?? p?.patient_age ?? p?.ageYears ?? p?.ageInYears;
  if (raw != null && raw !== "") { const n = Number(raw); return Number.isFinite(n) ? n : null; }
  if (p?.dob) { const dob = new Date(p.dob); if (!isNaN(dob)) return Math.floor((Date.now()-dob.getTime())/31557600000); }
  return null;
};
const formatGender = (p) => {
  const raw = String(p?.gender || p?.sex || p?.biologicalSex || p?.patientGender || "").trim().toLowerCase();
  if (!raw) return null;
  if (["m","male","man"].includes(raw)) return "Male";
  if (["f","female","woman"].includes(raw)) return "Female";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

/* --- sample data --------------------------------------- */
const SAMPLE_PATIENT = { patientName: "Priya Sharma", patientId: "PID-20489", age: 34, gender: "Female", tokenNo: "14" };
const SAMPLE_VITAL = {
  bloodPressureSystolic: 118, bloodPressureDiastolic: 76,
  heartRate: 72, temperature: 37.2, spO2: 98,
  respiratoryRate: 16, bloodSugar: 94, weight: 58, height: 162, bmi: 22.1,
  recordedAt: new Date().toISOString(),
};
const SAMPLE_HISTORY = Array.from({ length: 9 }, (_, i) => {
  const d = new Date(); d.setDate(d.getDate() - i * 7);
  return {
    recordedAt: d.toISOString(),
    bloodPressureSystolic: 108 + Math.floor(Math.random()*35),
    bloodPressureDiastolic: 68 + Math.floor(Math.random()*22),
    temperature: +(36.1 + Math.random()*2.2).toFixed(1),
    spO2: 93 + Math.floor(Math.random()*7),
    respiratoryRate: 13 + Math.floor(Math.random()*8),
    bloodSugar: 78 + Math.floor(Math.random()*70),
    weight: 57 + Math.floor(Math.random()*3),
    height: 162,
    bmi: +(21.3 + Math.random()*1.5).toFixed(1),
    notes: i % 3 === 0 ? "Follow-up visit" : i % 3 === 1 ? "Routine check" : "",
  };
});

/* --- Vital Card ---------------------------------------- */
function VitalCard({ icon: Icon, label, value, unit, status }) {
  const s = status ? STATUS[status] : null;
  return (
    <div style={{
      background: s ? s.bg : "#fafbfc",
      border: `1.5px solid ${s ? s.border : "#e8ecf0"}`,
      borderRadius: 12, padding: "13px 14px",
      display: "flex", flexDirection: "column", gap: 5,
      transition: "box-shadow .15s, transform .15s",
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,.08)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
        <div style={{
          width: 22, height: 22, borderRadius: 6,
          background: s ? s.badge : "#eef1f5",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon size={11} color={s ? s.text : "#64748b"} strokeWidth={2.2} />
        </div>
        <span style={{ fontSize: 9, fontWeight: 700, color: s ? s.badgeText : "#94a3b8", letterSpacing: ".7px", textTransform: "uppercase" }}>{label}</span>
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color: s ? s.text : "#1e293b", letterSpacing: -0.5, fontFamily: "'Outfit', sans-serif" }}>{value || "�"}</div>
      {unit && <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 500 }}>{unit}</div>}
    </div>
  );
}

/* --- History Table ------------------------------------- */
function HistoryTable({ vitalsHistory }) {
  const [filterDate, setFilterDate] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");

  const records = Array.isArray(vitalsHistory) ? vitalsHistory : [];
  const normalized = records.map((record) => {
    const timestamp = record.recordedAt || record.updatedAt || record.createdAt || record.appointmentDate || null;
    const dateObj = parseDateValue(timestamp);
    return {
      ...record,
      recordDate: dateObj,
      dateKey: dateObj ? dateObj.toISOString().slice(0, 10) : "",
    };
  });
  const sorted = [...normalized].sort((a, b) => {
    const ta = a.recordDate ? a.recordDate.getTime() : 0;
    const tb = b.recordDate ? b.recordDate.getTime() : 0;
    return sortOrder === "newest" ? tb - ta : ta - tb;
  });
  const filtered = filterDate ? sorted.filter(r => r.dateKey === filterDate) : sorted;

  const colored = (val, status) => {
    if (val == null || val === "") return <span style={{ color: "#cbd5e1" }}>�</span>;
    const s = status ? STATUS[status] : null;
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {status && (
          <span style={{
            display: "inline-block", width: 6, height: 6, borderRadius: "50%",
            background: STATUS[status].dot, flexShrink: 0,
          }} />
        )}
        <span style={{ fontWeight: 700, color: s ? s.text : "#374151" }}>{val}</span>
      </span>
    );
  };

  const inputStyle = {
    background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 8,
    padding: "6px 10px", fontSize: 12, color: "#374151",
    outline: "none", fontFamily: "'DM Sans', sans-serif",
    cursor: "pointer",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", fontFamily: "'Outfit', sans-serif" }}>Vitals History</div>
          <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 2 }}>All recorded visits at this clinic</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} style={inputStyle} />
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}
              style={{ ...inputStyle, paddingRight: 28, appearance: "none" }}>
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
            </select>
            <ChevronDown size={12} color="#94a3b8" style={{ position: "absolute", right: 9, pointerEvents: "none" }} />
          </div>
        </div>
      </div>

      <div style={{
        flex: 1, overflow: "auto", borderRadius: 12,
        border: "1.5px solid #e8ecf2",
        boxShadow: "0 1px 6px rgba(15,23,42,.04)",
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 850 }}>
          <thead>
            <tr style={{ background: "#f8fafc" }}>
              {["Date / Time","BP","Temp","HR","RR","SpO2","Weight","BMI","BS","Notes","Other measurements"].map((h, i) => (
                <th key={h} style={{
                  padding: "10px 14px", textAlign: i === 0 || i >= 9 ? "left" : "center",
                  fontWeight: 700, color: "#94a3b8", fontSize: 10, letterSpacing: ".6px",
                  textTransform: "uppercase", borderBottom: "1.5px solid #e8ecf2",
                  position: "sticky", top: 0, background: "#f8fafc", zIndex: 2,
                  whiteSpace: "nowrap",
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={11} style={{ textAlign: "center", padding: "48px 20px", color: "#94a3b8" }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>No records found</div>
              </td></tr>
            ) : filtered.map((v, i) => {
              const bpS = getBPStatus(v.bloodPressureSystolic, v.bloodPressureDiastolic);
              const tS = getTempStatus(Number(v.temperature));
              const hrS = getHRStatus(v.heartRate);
              const sS = getSpo2Status(v.spO2);
              const otherMeasurements = (Array.isArray(v.measurements) ? v.measurements : []).filter((item) => !CORE_MEASUREMENT_KEYS.has(item.measurementKey));
              const rowTime = v.recordDate || parseDateValue(v.recordedAt || v.updatedAt || v.createdAt || v.appointmentDate || "");
              const tdStyle = { padding: "10px 14px", textAlign: "center", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle" };
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc", transition: "background .1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafbfc"}
                >
                  <td style={{ ...tdStyle, textAlign: "left" }}>
                    <div style={{ fontWeight: 700, color: "#1e40af", fontSize: 11.5, fontFamily: "'Outfit',sans-serif" }}>{formatDate(rowTime)}</div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{formatTime(rowTime)}</div>
                  </td>
                  <td style={tdStyle}>{colored(v.bloodPressureSystolic && v.bloodPressureDiastolic ? `${v.bloodPressureSystolic}/${v.bloodPressureDiastolic}` : null, bpS)}</td>
                  <td style={tdStyle}>{colored(v.temperature != null ? `${v.temperature}�` : null, tS)}</td>
                  <td style={tdStyle}>{colored(v.heartRate != null ? `${v.heartRate} bpm` : null, hrS)}</td>
                  <td style={tdStyle}>{colored(v.respiratoryRate)}</td>
                  <td style={tdStyle}>{colored(v.spO2 != null ? `${v.spO2}%` : null, sS)}</td>
                  <td style={tdStyle}>{colored(v.weight != null ? `${v.weight} kg` : null)}</td>
                  <td style={tdStyle}>{colored(v.bmi)}</td>
                  <td style={tdStyle}>{colored(v.bloodSugar)}</td>
                  <td style={{ ...tdStyle, textAlign: "left", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 11.5, color: "#64748b", fontWeight: 400 }}>
                    {v.notes || <span style={{ color: "#cbd5e1" }}>�</span>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "left", minWidth: 190, maxWidth: 310, fontSize: 11, color: "#64748b", fontWeight: 400 }}>
                    {otherMeasurements.length ? otherMeasurements.map((item) => `${item.name}: ${displayMeasurement(item.value)}${item.unit ? ` ${item.unit}` : ""}`).join(" · ") : <span style={{ color: "#cbd5e1" }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --- Main ---------------------------------------------- */
export default function VisitReviewScreen({
  activePatient = SAMPLE_PATIENT,
  currentAppointmentVital = SAMPLE_VITAL,
  vitalsHistory = SAMPLE_HISTORY,
  onClose = () => {},
  onConfirm = () => {},
  isLoading = false,
}) {
  const age = resolveAge(activePatient);
  const gender = formatGender(activePatient);
  const pid = activePatient?.patientId ?? activePatient?.id ?? activePatient?.patient_id ?? "�";
  const v = currentAppointmentVital || {};

  const InfoRow = ({ icon: Icon, label, value }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderBottom: "1px solid #f1f5f9" }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0f4ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={13} color="#6366f1" strokeWidth={2} />
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: ".5px", textTransform: "uppercase", width: 72, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: "#1e293b", fontFamily: "'Outfit', sans-serif" }}>{value || "�"}</span>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes slideUp { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: #f8fafc; }
        ::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
        select option { background: #fff; color: #374151; }
      `}</style>

      <div style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "#f5f7fa",
        fontFamily: "'DM Sans', sans-serif",
        color: "#1e293b",
        display: "flex", flexDirection: "column",
        height: "100vh", overflow: "hidden",
        animation: "slideUp .2s ease",
      }}>

        {/* HEADER */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 28px", height: 58, flexShrink: 0,
          background: "#fff",
          borderBottom: "1.5px solid #e8ecf2",
          boxShadow: "0 1px 4px rgba(15,23,42,.05)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 11,
              background: "linear-gradient(135deg,#4f46e5,#818cf8)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 12px rgba(99,102,241,.28)",
            }}>
              <Activity size={17} color="#fff" strokeWidth={2.5} />
            </div>
            <div>
              <div style={{ fontFamily: "'Outfit',sans-serif", fontSize: 16, fontWeight: 800, color: "#0f172a", letterSpacing: -.3 }}>
                Review & Complete Visit
              </div>
              <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 1 }}>Confirm vitals before closing this appointment</div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              background: "#f8fafc", border: "1.5px solid #e2e8f0",
              borderRadius: 10, padding: "7px 14px",
            }}>
              <div style={{
                width: 30, height: 30, borderRadius: 9,
                background: "linear-gradient(135deg,#e0e7ff,#c7d2fe)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <User size={14} color="#4f46e5" strokeWidth={2} />
              </div>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a", fontFamily: "'Outfit',sans-serif" }}>{activePatient?.patientName || "�"}</div>
                <div style={{ fontSize: 10, color: "#94a3b8" }}>Token #{activePatient?.tokenNo || "�"} � {pid}</div>
              </div>
            </div>

            <button onClick={onClose} style={{
              width: 36, height: 36, borderRadius: 9,
              border: "1.5px solid #e2e8f0", background: "#fff",
              color: "#94a3b8", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all .15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#cbd5e1"; e.currentTarget.style.color = "#64748b"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#94a3b8"; }}
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* BODY */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>

          {/* LEFT SIDEBAR */}
          <div style={{
            width: 268, flexShrink: 0,
            borderRight: "1.5px solid #e8ecf2",
            background: "#fff",
            display: "flex", flexDirection: "column",
            overflow: "hidden",
          }}>
            <div style={{ padding: "16px 20px 12px", borderBottom: "1.5px solid #f1f5f9", flexShrink: 0 }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".8px", textTransform: "uppercase", marginBottom: 10 }}>Patient Info</div>
              <InfoRow icon={User} label="Name" value={activePatient?.patientName} />
              <InfoRow icon={Hash} label="Patient ID" value={pid} />
              <InfoRow icon={Calendar} label="Age" value={age != null ? `${age} yrs` : null} />
              <InfoRow icon={User} label="Sex" value={gender} />
            </div>

            <div style={{ flex: 1, padding: "14px 20px", display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexShrink: 0 }}>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", letterSpacing: ".8px", textTransform: "uppercase" }}>Current Visit</div>
                {(v.recordedAt || v.updatedAt) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9.5, color: "#64748b", background: "#f8fafc", padding: "3px 8px", borderRadius: 6, border: "1px solid #e8ecf2" }}>
                    <Clock size={9} />
                    {formatTime(v.recordedAt || v.updatedAt)}
                  </div>
                )}
              </div>

              {isLoading ? (
                <div style={{ color: "#94a3b8", fontSize: 12, textAlign: "center", marginTop: 24 }}>Loading vitals�</div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, overflowY: "auto", paddingRight: 2, paddingBottom: 4 }}>
                  {Array.isArray(v.measurements) ? (
                    v.measurements.length
                      ? v.measurements.map((item) => <VitalCard key={item.id ?? item.measurementKey} icon={Activity} label={item.name || "Measurement"} value={displayMeasurement(item.value)} unit={item.unit || ""} />)
                      : <div style={{ gridColumn: "span 2", textAlign: "center", color: "#94a3b8", padding: "24px 0", fontSize: 12 }}>No measurements recorded</div>
                  ) : (
                    <>
                      {v.bloodPressureSystolic && <VitalCard icon={Droplets} label="BP" value={`${v.bloodPressureSystolic}/${v.bloodPressureDiastolic}`} unit="mmHg" status={getBPStatus(v.bloodPressureSystolic, v.bloodPressureDiastolic)} />}
                      {v.heartRate && <VitalCard icon={Heart} label="Heart Rate" value={`${v.heartRate}`} unit="bpm" status={getHRStatus(v.heartRate)} />}
                      {v.temperature && <VitalCard icon={Thermometer} label="Temp" value={`${v.temperature}°`} unit="°C" status={getTempStatus(Number(v.temperature))} />}
                      {v.spO2 && <VitalCard icon={Activity} label="SpO2" value={`${v.spO2}%`} unit="%" status={getSpo2Status(v.spO2)} />}
                      {v.respiratoryRate && <VitalCard icon={Wind} label="Resp Rate" value={`${v.respiratoryRate}`} unit="br/min" />}
                      {v.bloodSugar && <VitalCard icon={Zap} label="Blood Sugar" value={`${v.bloodSugar}`} unit="mg/dL" />}
                      {v.weight && <VitalCard icon={Weight} label="Weight" value={`${v.weight} kg`} />}
                      {v.height && <VitalCard icon={Ruler} label="Height" value={`${v.height} cm`} />}
                      {v.bmi && <VitalCard icon={Activity} label="BMI" value={`${v.bmi}`} unit="kg/m²" />}
                      {!v.bloodPressureSystolic && !v.heartRate && !v.temperature && <div style={{ gridColumn: "span 2", textAlign: "center", color: "#94a3b8", padding: "24px 0", fontSize: 12 }}>No vitals recorded</div>}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT � HISTORY */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", padding: "20px 24px", minWidth: 0, overflow: "hidden" }}>
            <HistoryTable vitalsHistory={vitalsHistory} />
          </div>
        </div>

        {/* FOOTER */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 28px", height: 54, flexShrink: 0,
          background: "#fff",
          borderTop: "1.5px solid #e8ecf2",
          boxShadow: "0 -1px 4px rgba(15,23,42,.04)",
        }}>
          <div style={{ display: "flex", gap: 18, fontSize: 11 }}>
            {[["normal","Normal"],["warning","Borderline"],["critical","Abnormal"]].map(([s,l]) => (
              <span key={s} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS[s].dot, display: "inline-block" }} />
                <span style={{ color: "#64748b", fontWeight: 500 }}>{l}</span>
              </span>
            ))}
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={{
              padding: "0 20px", height: 38, border: "1.5px solid #e2e8f0",
              borderRadius: 10, background: "#fff", color: "#64748b",
              fontWeight: 600, fontSize: 13, cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif", transition: "all .15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
            >Back</button>

            <button onClick={onConfirm} style={{
              padding: "0 22px", height: 38, border: "none", borderRadius: 10,
              background: "linear-gradient(135deg,#4f46e5,#818cf8)",
              color: "#fff", fontWeight: 700, fontSize: 13,
              cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
              display: "inline-flex", alignItems: "center", gap: 8,
              boxShadow: "0 4px 14px rgba(99,102,241,.35)",
              transition: "all .15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(99,102,241,.45)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 4px 14px rgba(99,102,241,.35)"; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <CheckCircle2 size={15} />
              Complete Visit
            </button>
          </div>
        </div>

      </div>
    </>
  );
}

